import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../../config/config";
import { getToken } from "../../../utils/localStorageMethods";
import { translateManyClient } from "../../../utils/ItClient";
import i18n from "../i18n";

const apiUrl = config.Url;
const norm = (l) => (l || "en").slice(0, 2);

// Create a baseQuery with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: `${apiUrl}/`,
  credentials: "include",
  prepareHeaders: (headers) => {
    const token = getToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
      headers.set("ngrok-skip-browser-warning", "true");
    }
    return headers;
  },
});

export const ratingSlice = createApi({
  reducerPath: "ratingSlice",
  baseQuery: baseQuery,
  tagTypes: ["rating"],
  endpoints: (builder) => ({
    // GET Rating List
    getRating: builder.query({
      query: ({ page, limit, rating, search, role }) => {
        const params = new URLSearchParams();

        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);
        if (rating) params.append("rating", rating);
        if (search) params.append("search", search);
        if (role) params.append("role", role);

        const queryString = params.toString();
        return queryString ? `rating-reviews?${queryString}` : "rating-reviews";
      },

      // Dynamic Data Localization
      async onQueryStarted(arg, { queryFulfilled, dispatch }) {
        try {
          const { data } = await queryFulfilled;

          const lang = norm(i18n.language || "en");
          if (lang === "en") return;

          const rating = data?.data?.ratingRecords ?? [];
          if (!Array.isArray(rating) || rating.length === 0) return;

          // Collect strings to translate (skip PII like names/emails)
          const bucket = [];
          for (const review of rating) {
            if (review.comment) bucket.push(review.comment);
            if (review.vehicle.vehicle_type)
              bucket.push(review.vehicle.vehicle_type);
            if (review.user.full_name) bucket.push(review.user.full_name);
            if (review.vendor.full_name) bucket.push(review.vendor.full_name);
            if (review.driver.full_name) bucket.push(review.driver.full_name);
          }

          const map = await translateManyClient(bucket, lang, "en");
          const pick = (s) => (typeof s === "string" ? (map.get(s) ?? s) : s);

          dispatch(
            ratingSlice.util.updateQueryData("getRating", arg, (draft) => {
              const arr = draft?.data?.ratingRecords ?? [];
              arr.forEach((rating) => {
                rating.comment = pick(rating.comment);
                rating.vehicle.vehicle_type = pick(rating.vehicle.vehicle_type);
                rating.user.full_name = pick(rating.user.full_name);
                rating.vendor.full_name = pick(rating.vendor.full_name);
                rating.driver.full_name = pick(rating.driver.full_name);
              });
            }),
          );
        } catch {
          /* ignore translation errors */
        }
      },

      providesTags: ["rating"],
    }),
    getDriverDetails: builder.query({
      query: (id) => `${id}`,
      providesTags: ["rating"],
    }),
    // Create New Driver
    createDriver: builder.mutation({
      query: (data) => ({
        url: "",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["rating"],
    }),
    // Update Driver
    updateDriver: builder.mutation({
      query: ({ data, id }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["rating"],
    }),
    // DELETE a Driver
    deleteDriver: builder.mutation({
      query: (id) => ({
        url: `${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["rating"],
    }),
  }),
});

export const {
  useGetRatingQuery,
  useGetDriverDetailsQuery,
  useCreateDriverMutation,
  useUpdateDriverMutation,
  useDeleteDriverMutation,
} = ratingSlice;
